import {useMutation, useQueryClient} from '@tanstack/react-query'
import {Button} from 'primereact/button'
import {Dialog} from 'primereact/dialog'
import {Divider} from 'primereact/divider'
import {Image} from 'primereact/image'
import {InputTextarea} from 'primereact/inputtextarea'
import {MultiSelect} from 'primereact/multiselect'
import {Sidebar} from 'primereact/sidebar'
import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useState,
} from 'react'
import {createOrUpdateGenericInfo} from '../../api/ServiceWorker'
import TranslateUtils from '../../i18n/TranslateUtils'
import {QueryKeyManager} from '../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {useAuth} from '../authentication/AuthProvider'
import FormHeader from '../form/FormHeader'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {refetchEvent} from '../table-utils/TableUtils'
import {useSymptomsPathwayByUser} from '../user-property/query-keys/UserPropertyQueryManager'
import {UserPropertyType} from '../user-property/UserPropertyConstants'
import PatientMedicalHistory from './PatientMedicalHistory'

const PATH = 'userSymptom'
const QueryKey = 'User-Symptom'
const SymptomQueryKey = 'Symptom-By-Pathway'
const PatientSymptomForm = forwardRef(({onChange = () => {}}, ref) => {
    useImperativeHandle(ref, () => ({
        showForm: () => showForm(),
        hideForm: () => hideForm(),
    }))
    const queryClient = useQueryClient()
    const {translateCommons} = TranslateUtils()
    const {user} = useAuth()
    const [selected, setSelected] = useState([])

    const {data: symptomData, refetch} = useSymptomsPathwayByUser(
        user?.id,
        user?.id,
        !!user?.id,
    )

    const symptomOptions = useMemo(() => {
        if (
            !symptomData ||
            !Array.isArray(symptomData) ||
            symptomData?.length < 1
        ) {
            return []
        }

        let options = []
        for (let i = 0; i < symptomData?.length; i++) {
            let item = {
                label: translateCommons(symptomData[i]),
                value: symptomData[i],
            }
            options.push(item)
        }
        return options
    }, [symptomData, translateCommons])

    const [visible, setVisible] = useState(false)
    const [visible2, setVisible2] = useState(false)

    const [symptom, setSymptom] = useState('')
    const showForm = () => {
        blockOverflownScroll()
        setVisible(true)
    }

    const hideForm = () => {
        unblockOverflownScroll()
        setVisible(false)
    }

    const {mutate: handleCreateOrUpdate} = useMutation(
        data => createOrUpdateGenericInfo(PATH, user?.id, data),
        {
            onSuccess: data => {
                if (!!data?.id) {
                    const {userPropertyKeys} = QueryKeyManager(
                        QueryKeyTypes.userProperty,
                        user?.id,
                    )

                    queryClient.invalidateQueries(
                        userPropertyKeys.pattern({
                            type: UserPropertyType.userSymptom,
                        }),
                    )

                    // queryClient.setQueryData(
                    //     [QueryKey, user?.id, user?.id],
                    //     oldData => {
                    //         if (!oldData || !oldData.content) {
                    //             return {
                    //                 content: [data],
                    //             }
                    //         } else {
                    //             return {
                    //                 ...oldData,
                    //                 content: [data, ...oldData.content],
                    //             }
                    //         }
                    //     },
                    // )
                    setVisible2(true)
                    setSymptom('')
                    setSelected([])
                    showNotification(
                        severityNotification.success,
                        'Symptom reported !!',
                    )
                }
            },
        },
    )
    const getSelectValue = (select, string) => {
        let str = ''
        if (select && Array.isArray(select) && select.length > 0) {
            str = select.join(', ')
            if (string) {
                str =
                    str +
                    ', ' +
                    string.charAt(0).toUpperCase() +
                    string.slice(1)
            }
        } else {
            str = string.charAt(0).toUpperCase() + string.slice(1)
        }

        return str
    }

    const handleSubmit = useCallback(() => {
        let toPostData = {
            symptom: getSelectValue(selected, symptom),
            userId: user?.id ? user?.id : null,
        }
        handleCreateOrUpdate(toPostData)
    }, [handleCreateOrUpdate, symptom, user?.id, selected])

    const handleOnSelect = e => {
        setSelected(e.value)
    }

    useEffect(() => {
        if (!!SymptomQueryKey && !!user?.id) {
            refetchEvent.on(
                'REFETCH-DATA-TABLE-' + SymptomQueryKey + user?.id,
                () => {
                    refetch()
                },
            )

            return () => {
                refetchEvent.removeListener(
                    'REFETCH-DATA-TABLE-' + SymptomQueryKey + user?.id,
                )
            }
        }
    }, [refetch, user?.id])

    return (
        <Sidebar
            className="p-sidebar-full "
            visible={visible}
            blockScroll
            style={{background: '#f9f9g9'}}
            onHide={() => hideForm()}
            fullScreen>
            <div>
                <FormHeader
                    header={translateCommons('SYMPTOM FORM')}
                    onHide={() => hideForm()}
                />
                <div className="flex col-12 lg:col-12 md:col-12 mt-3 align-items-center justify-content-center">
                    <div
                        className="grid flex-column sm:flex-column md:flex-row lg:flex-row col-12 surface-card border-round shadow-2 main-radius p-0"
                        style={{minHeight: '80vh'}}>
                        <div className="col-12 sm:col-12 md:col-12 lg:col-8 grid">
                            <div className="col-12 text-center">
                                <Image
                                    height="200px"
                                    src={require('./../../assets/images/Observation.jpg')}
                                />
                            </div>
                            <div className="flex col-12 text-center justify-content-center text-medium">
                                <div className="col-8">
                                    <h1
                                        className="font-bold col-12 p-0"
                                        style={{
                                            color: 'rgb(23,187,173)',
                                        }}>
                                        {translateCommons(
                                            'Report your symptoms',
                                        )}
                                    </h1>
                                    <p className="col-12 p-0">
                                        {translateCommons(
                                            "You can either choose from a selection using 'Choose your symptom' button down below or type what you're feeing in the textbox.",
                                        )}{' '}
                                    </p>
                                    <p className="col-12 p-0 mt-0">
                                        {translateCommons(
                                            "Click 'Submit' when you are done with your choice.",
                                        )}
                                    </p>
                                </div>
                            </div>

                            <div className="col-12 ">
                                <MultiSelect
                                    className="col-12 h-3rem p-0"
                                    value={selected || []}
                                    options={symptomOptions}
                                    onChange={handleOnSelect}
                                    optionLabel="label"
                                    optionValue="value"
                                    placeholder={translateCommons(
                                        'Choose your symptom',
                                    )}
                                    // maxSelectedLabels={}
                                    emptyFilterMessage={translateCommons(
                                        'No results found',
                                    )}
                                    resetFilterOnHide
                                    display="chip"
                                    filter
                                    filterBy="label"
                                    filterPlaceholder={translateCommons(
                                        'Search here',
                                    )}
                                    style={{
                                        '--inputColor':
                                            'rgba(68, 184, 166, 0.1)',
                                        backgroundColor:
                                            'rgba(68, 184, 166, 0.1)',
                                        borderColor: 'rgba(68, 184, 166, 0.1)',
                                    }}
                                />
                                <div className="col-12 m-0">
                                    <i className="m-0">
                                        {translateCommons(
                                            'Note: You can either choose from the suggestion list or manually enter your symptoms',
                                        )}
                                    </i>
                                </div>
                            </div>
                            <Divider
                                className="m-0"
                                align="center"
                                layout="horizontal">
                                <span className="p-tag px-3 ">
                                    {translateCommons('And')}
                                </span>
                            </Divider>
                            <div className="col-12 ">
                                <InputTextarea
                                    placeholder={translateCommons(
                                        'Enter symptoms',
                                    )}
                                    className="col-12 h-9rem"
                                    value={symptom}
                                    onChange={e => setSymptom(e.target.value)}
                                />
                                <div className="col-12">
                                    <i className="m-0">
                                        {translateCommons(
                                            'Note: Besides, you can provide us more detailed information about your health condition by inputting the box above',
                                        )}
                                    </i>
                                </div>
                            </div>
                            <div className="col-12">
                                <Button
                                    label={translateCommons('Submit')}
                                    className="col-12 h-3rem"
                                    onClick={handleSubmit}
                                />
                            </div>
                        </div>
                        <div className="col-12 sm:col-12 md:col-12 lg:col-4">
                            <PatientMedicalHistory userId={user?.id} />
                        </div>
                    </div>
                    <Dialog
                        visible={visible2}
                        onHide={() => setVisible2(false)}
                        modal
                        breakpoints={{'960px': '75vw', '640px': '100vw'}}
                        style={{width: '40vw'}}
                        closable={false}
                        showHeader={false}
                        footer={
                            <div className="surface-border pt-3 flex">
                                <Button
                                    icon="pi pi-times"
                                    onClick={() => setVisible2(false)}
                                    label={translateCommons('Close')}
                                    className=" w-12 "
                                />
                            </div>
                        }>
                        <div className="flex flex-column align-items-center my-4">
                            <span
                                className="flex align-items-center justify-content-center bg-cyan-100 text-cyan-800 mr-3 border-circle mb-3"
                                style={{width: '64px', height: '64px'}}>
                                <i className="pi pi-check text-5xl"></i>
                            </span>
                            <div className="font-medium text-2xl text-green-700">
                                {translateCommons('Submit Successfully')}
                            </div>
                        </div>
                        <p className="line-height-3 p-0 m-0">
                            {/* Sagittis eu volutpat odio facilisis mauris sit amet.
                            Sed velit dignissim sodales ut eu sem integer. Sed
                            risus pretium quam vulputate. At tellus at urna
                            condimentum mattis pellentesque. */}
                        </p>
                    </Dialog>
                </div>
            </div>
        </Sidebar>
    )
})

export default PatientSymptomForm
